import React from 'react';
import ReactDOM from 'react-dom';
import { useQuery } from 'urql';
import CONTENTO_TYPES from '../../domain/ContentoTypes';
import { isBrowser } from '../../utils/environment';
import { createImageProxyUrl } from '../../utils/svtimage';
import VideoPlayerHandlerContainer from '../VideoPlayer/new/VideoPlayerHandlerContainer';
import VIDEO_MODAL_QUERY from './VideoModal.graphql';
import VideoModalError from './VideoModalError';
import { Container, SlashAnimation, SlashAnimationWrapper } from './VideoModal.styles';
import FocusTrap from 'focus-trap-react';

const VideoModalPlay = ({ data, id }) => {
    if (!data || !data.detailsPage) {
        return null;
    }

    const {
        detailsPage: {
            item: {
                barn,
                restrictions: { blockedForChildren },
                parent,
                name: episodeTitle,
                __typename,
                tags,
                highlights,
                urls: { svtplay: url },
                videos,
            },
            svtId,
            id: visionId,
            images,
        },
    } = data;

    const typeName =
        __typename === CONTENTO_TYPES.VARIANT || __typename === CONTENTO_TYPES.CLIP
            ? parent
                ? parent.__typename
                : __typename
            : __typename;

    const posterImage = createImageProxyUrl(images.wide.id, images.wide.changed, 'large', 1024);

    const isKidsGenre = tags.some((tag) => tag.slug === 'barn' || tag.slug === 'barn-oppet-arkiv');

    return (
        <VideoPlayerHandlerContainer
            barn={barn}
            visionId={visionId}
            escenicId={id}
            titleArticleId={parent ? parent.id : null}
            title={parent ? parent.name : episodeTitle}
            episodeTitle={episodeTitle}
            posterImage={posterImage}
            autoPlay={true}
            blockedForChildren={blockedForChildren}
            svtId={svtId}
            __typename={typeName}
            __videoType={__typename}
            isKidsGenre={isKidsGenre}
            highlights={highlights}
            url={url}
            videos={videos}
            parentUrl={parent.urls.svtplay}
        />
    );
};

const VideoModal = ({ id }) => {
    const [result] = useQuery({
        query: VIDEO_MODAL_QUERY,
        variables: {
            id: id,
        },
    });

    const { data, error, fetching } = result;

    // Video player break if no parent is present
    const hasData = Boolean(data?.detailsPage?.item?.parent);
    const isError = error || (!hasData && !fetching);
    const shouldRender = isError || hasData; // FocusTrap can only render if there is something focusable

    return ReactDOM.createPortal(
        <>
            <SlashAnimationWrapper>
                <SlashAnimation />
            </SlashAnimationWrapper>
            {shouldRender && (
                <main role="main">
                    <Container role="dialog" aria-modal="true" aria-label="video modal">
                        {hasData && <VideoModalPlay data={data} id={id} />}
                        {isError && (
                            <FocusTrap>
                                <VideoModalError data={data} error={error} id={id} />
                            </FocusTrap>
                        )}
                    </Container>
                </main>
            )}
        </>,
        document.getElementById('__next')
    );
};

// Component split in two allow early return before useQuery hook.
const VideoModalContainer = ({ id }) => {
    if (!id || !isBrowser()) {
        return null;
    }

    return (
        <>
            <VideoModal id={id} />
        </>
    );
};

export default VideoModalContainer;
